import React, { useEffect, useState } from 'react';
import { Zap, AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui';
import { Badge } from '@/components/ui';
import { Spinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { apiRequest } from '@/services/api';
import { useToast } from '@/contexts/ToastContext';
import { getPlans, type PlanRecord } from '@/services/billingService';

interface FeatureRow {
  label: string;
  value: string;
  included: boolean;
}

/** Rank known tiers so we can render "Downgrade" vs "Upgrade". Unknown ids rank 0. */
const PLAN_RANK: Record<string, number> = { starter: 1, prototyper: 1, business: 2, enterprise: 3 };

function formatPrice(plan: PlanRecord): string {
  const base = plan.pricing?.basePriceUsd ?? 0;
  if (base === 0 && plan.id === 'enterprise') return 'Custom';
  return `$${base.toLocaleString()}`;
}

function priceNote(plan: PlanRecord): string {
  const base = plan.pricing?.basePriceUsd ?? 0;
  if (base === 0 && plan.id === 'enterprise') return 'contact us';
  return 'per month';
}

/** Build a customer-facing feature list from a plan's entitlements. */
function featuresFor(plan: PlanRecord): FeatureRow[] {
  const e = plan.entitlements;
  const rows: FeatureRow[] = [
    {
      label: 'Tokens / month',
      value: e.tokensUnlimited ? 'Unlimited' : e.tokensPerMonth.toLocaleString(),
      included: e.tokensUnlimited || e.tokensPerMonth > 0,
    },
    {
      label: 'Private edge devices',
      value: !e.myDevices.enabled
        ? '—'
        : e.myDevices.maxDevices != null
          ? `Up to ${e.myDevices.maxDevices}`
          : 'Unlimited',
      included: e.myDevices.enabled,
    },
    {
      label: 'Reporting',
      value: e.reportTier.charAt(0).toUpperCase() + e.reportTier.slice(1),
      included: true,
    },
    {
      label: 'Team members',
      value: e.maxUsers === 0 ? 'Unlimited' : `Up to ${e.maxUsers}`,
      included: true,
    },
    {
      label: 'Scheduled tests',
      value: e.schedulesEnabled ? '✓' : '—',
      included: e.schedulesEnabled,
    },
    {
      label: 'API access',
      value: e.apiAccess ? '✓' : '—',
      included: e.apiAccess,
    },
    {
      label: 'AI assistant',
      value: e.aiAssistant ? '✓' : '—',
      included: e.aiAssistant,
    },
  ];
  return rows;
}

interface Props {
  open: boolean;
  onClose: () => void;
  currentPlan?: string;
  context?: 'upgrade' | 'quota_exceeded';
}

export function UpgradePlanModal({ open, onClose, currentPlan = 'starter', context = 'upgrade' }: Props) {
  const { activeOrganization } = useAuth();
  const { error: toastError } = useToast();
  const [loading, setLoading] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanRecord[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setPlansLoading(true);
      try {
        const fetched = await getPlans();
        if (!cancelled) setPlans(fetched);
      } catch (err: any) {
        console.error('[UpgradePlanModal] failed to load plans:', err);
        if (!cancelled) toastError(err?.message ?? 'Failed to load plans. Please try again.');
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function handleCheckout(plan: string) {
    if (!activeOrganization?.id) return;
    setLoading(plan);
    try {
      const { url } = await apiRequest<{ url: string }>(
        '/billing/create-checkout-session',
        {
          method: 'POST',
          body: JSON.stringify({
            plan,
            returnBaseUrl: window.location.origin,
          }),
        },
        activeOrganization.id,
      );
      window.location.href = url;
    } catch (err: any) {
      console.error('[UpgradePlanModal] checkout error:', err);
      toastError(err?.message ?? 'Failed to start checkout. Please try again.');
      setLoading(null);
    }
  }

  const currentRank = PLAN_RANK[currentPlan] ?? 0;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={context === 'quota_exceeded' ? 'Token balance exhausted' : 'Change your plan'}
      size="xl"
    >
      <div className="space-y-4">
        {context === 'quota_exceeded' && (
          <div className="flex items-start gap-2.5 rounded-lg border border-status-error/30 bg-status-error-bg px-4 py-3">
            <AlertTriangle className="w-4 h-4 text-status-error mt-0.5 shrink-0" />
            <p className="text-sm text-status-error">
              You've run out of tokens for this period. Upgrade to keep running tests.
            </p>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Choose a plan to unlock more tokens and features.
        </p>

        {plansLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : plans.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No plans are available right now.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {plans.map((plan) => {
              const isCurrent = currentPlan === plan.id;
              const isLoading = loading === plan.id;
              const planRank = PLAN_RANK[plan.id] ?? 0;
              const isDowngrade = !isCurrent && planRank < currentRank;
              const base = plan.pricing?.basePriceUsd ?? 0;
              const isContact = base === 0 && plan.id === 'enterprise';
              const features = featuresFor(plan);

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-xl border p-5 flex flex-col gap-4 ${
                    isCurrent ? 'border-accent-blue bg-accent-blue/5' : 'border-border bg-card'
                  }`}
                >
                  {isCurrent && (
                    <Badge className="absolute top-3 right-3 text-xs bg-accent-blue/10 text-accent-blue">
                      Current
                    </Badge>
                  )}

                  <div>
                    <h3 className="text-base font-semibold text-foreground">{plan.displayName}</h3>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-2xl font-bold text-foreground">{formatPrice(plan)}</span>
                      <span className="text-sm text-muted-foreground">{priceNote(plan)}</span>
                    </div>
                  </div>

                  <ul className="space-y-2 flex-1">
                    {features.map((feat) => (
                      <li key={feat.label} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-muted-foreground">{feat.label}</span>
                        <span
                          className={`font-medium shrink-0 ${
                            feat.included ? 'text-foreground' : 'text-muted-foreground/50'
                          }`}
                        >
                          {feat.value}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {isContact ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => window.open('mailto:sales@simcheck.ai', '_blank')}
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                      Contact us
                    </Button>
                  ) : (
                    <Button
                      variant={isDowngrade ? 'outline' : 'default'}
                      size="sm"
                      className="w-full"
                      disabled={isCurrent || isLoading || loading !== null}
                      onClick={() => handleCheckout(plan.id)}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          Redirecting…
                        </>
                      ) : isCurrent ? (
                        'Current plan'
                      ) : isDowngrade ? (
                        `Downgrade to ${plan.displayName}`
                      ) : (
                        <>
                          <Zap className="w-3.5 h-3.5 mr-1.5" />
                          Upgrade to {plan.displayName}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
